import { User } from 'firebase/app'
import { PrometheusTheme } from '@prometheus/types'
import { DEFAULT_THEME } from '../../utils/theme'
import { GuestSession } from '../../utils/guestSession'

/**
 * Who is playing.
 *
 * There are two ways to have an identity: signed in with Google, or playing as
 * a guest off a play key (see `utils/guestSession`). The rest of the app only
 * ever asks for `uid` and `authorized`, so both are resolved onto the same flat
 * fields here and the raw slots (`user` / `guestSession`) stay internal.
 *
 * A signed-in account always wins over a guest session: signing in while a
 * guest session is stored switches you to the account, and the guest's games
 * stay reachable through their play link.
 */

interface AuthUser {
  uid: string
  displayName?: string | null
  email?: string | null
}

interface AuthState {
  /** Has a playable identity — signed in OR a guest. Gates every play action. */
  authorized: boolean
  /** Signed in with a real account, as opposed to playing as a guest. */
  signedIn: boolean
  /** Playing without an account. */
  guest: boolean
  /** The guest's play key, for the socket handshake and the play link. */
  guestToken?: string
  name?: string | null
  email?: string | null
  uid?: string | null
  admin?: boolean
  displayName?: string | null
  connected: boolean
  theme: PrometheusTheme
  user?: AuthUser | null
  guestSession?: GuestSession | null
}

const initialState: AuthState = {
  authorized: false,
  signedIn: false,
  guest: false,
  connected: false,
  theme: DEFAULT_THEME,
}

export const AUTH_STATE_CHANGED = 'auth/AUTH_STATE_CHANGED'
export const GUEST_SESSION_CHANGED = 'auth/GUEST_SESSION_CHANGED'
export const SET_DISPLAY_NAME = 'auth/SET_DISPLAY_NAME'
export const SET_CONNECTED = 'auth/SET_CONNECTED'
export const SET_ADMIN = 'auth/SET_ADMIN'
export const SET_THEME = 'auth/SET_THEME'

/**
 * Fold the account / guest slots into the flat identity the app reads. A name
 * and an admin flag belong to ONE identity, so switching identity drops them
 * rather than letting the previous player's details linger.
 */
const resolveIdentity = (state: AuthState): AuthState => {
  const { user, guestSession } = state

  const identity = user
    ? {
      uid: user.uid,
      name: user.displayName ?? null,
      email: user.email ?? null,
      guest: false,
      guestToken: undefined,
      displayName: undefined,
    }
    : guestSession
      ? {
        uid: guestSession.uid,
        name: guestSession.name ?? null,
        email: null,
        guest: true,
        guestToken: guestSession.token,
        displayName: guestSession.name ?? null,
      }
      : {
        uid: undefined,
        name: undefined,
        email: undefined,
        guest: false,
        guestToken: undefined,
        displayName: undefined,
      }

  const switchedIdentity = (state.uid ?? undefined) !== (identity.uid ?? undefined)

  return {
    ...state,
    uid: identity.uid,
    name: identity.name,
    email: identity.email,
    guest: identity.guest,
    guestToken: identity.guestToken,
    signedIn: !!user,
    authorized: !!identity.uid,
    displayName: switchedIdentity ? identity.displayName : (identity.displayName ?? state.displayName),
    admin: switchedIdentity ? false : state.admin,
  }
}

export default function (state: AuthState = initialState, action: AuthActionTypes): AuthState {
  switch (action.type) {
  case AUTH_STATE_CHANGED:
    return resolveIdentity({
      ...state,
      user: action.payload ? {
        uid: action.payload.uid,
        displayName: action.payload.displayName,
        email: action.payload.email,
      } : null,
    })
  case GUEST_SESSION_CHANGED:
    return resolveIdentity({
      ...state,
      guestSession: action.payload ?? null,
    })
  case SET_DISPLAY_NAME:
    return {
      ...state,
      displayName: action.payload ?? 'Anonymous',
      // Keep the guest session in step so a rename survives a reload.
      guestSession: state.guestSession
        ? { ...state.guestSession, name: action.payload ?? state.guestSession.name }
        : state.guestSession,
    }
  case SET_CONNECTED:
    return {
      ...state,
      connected: action.payload,
    }
  case SET_ADMIN:
    return {
      ...state,
      admin: true,
    }
  case SET_THEME:
    return {
      ...state,
      theme: action.payload,
    }
  default:
    return state
  }
}

interface AuthStateChangedAction {
  type: typeof AUTH_STATE_CHANGED
  payload: User | null
}

interface GuestSessionChangedAction {
  type: typeof GUEST_SESSION_CHANGED
  payload: GuestSession | null
}

interface SetDisplayNameAction {
  type: typeof SET_DISPLAY_NAME
  payload?: string
}

interface SetConnectedAction {
  type: typeof SET_CONNECTED
  payload: boolean
}

interface SetAdminAction {
  type: typeof SET_ADMIN
}

interface SetThemeAction {
  type: typeof SET_THEME
  payload: PrometheusTheme
}

export function updateAuthorization(user: User | null): AuthStateChangedAction {
  return {
    type: AUTH_STATE_CHANGED,
    payload: user,
  }
}

export function setGuestSession(session: GuestSession | null): GuestSessionChangedAction {
  return {
    type: GUEST_SESSION_CHANGED,
    payload: session,
  }
}

export function setDisplayName(name?: string): SetDisplayNameAction {
  return {
    type: SET_DISPLAY_NAME,
    payload: name,
  }
}

export function setAdmin(): SetAdminAction {
  return { type: SET_ADMIN }
}

export function setConnected(connected: boolean): SetConnectedAction {
  return {
    type: SET_CONNECTED,
    payload: connected,
  }
}

export function setTheme(theme: PrometheusTheme): SetThemeAction {
  return {
    type: SET_THEME,
    payload: theme,
  }
}

export type AuthActionTypes =
  | AuthStateChangedAction
  | GuestSessionChangedAction
  | SetDisplayNameAction
  | SetConnectedAction
  | SetAdminAction
  | SetThemeAction
